import type { SubmitHandler } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Labeled,
  Stack,
  Step,
  TextInputField,
} from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import type { IPackagePreparerStepProps } from './PackagePreparerStep';
import { FormField } from '~/components/atoms/FormField';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUpdateAppMutation } from '~/lib/api/modules/Apps/hooks/useUpdateAppMutation';

export type IPackagePreparerNameStepProps = IPackagePreparerStepProps;

export type IPackagePreparerNameStepFormValue = {
  name?: string;
};

const styles = stylex.create({
  field: {
    width: 400,
  },
});

export const PackagePreparerNameStep: React.FC<
  IPackagePreparerNameStepProps
> = (props) => {
  const { appId, onSuccess, platform, ...other } = props;
  const { t } = useTranslation();
  const appQuery = useAppQuery({ appId });
  const updateMutation = useUpdateAppMutation();
  const form = useForm<IPackagePreparerNameStepFormValue>();
  const [isUniqueName, setIsUniqueName] = useState(false);
  const app = appQuery.data;
  const name = app?.builds?.[platform]?.name;
  const defaultName = name ?? app?.name;
  const locked = app?.locked?.[platform === 'ios' ? 'iosName' : 'androidName'];
  const prefix =
    import.meta.env.MODE !== 'prod'
      ? app
        ? `${import.meta.env.MODE}-${app._id.slice(-4)}-`
        : `${import.meta.env.MODE}-`
      : undefined;

  const isInitRef = useRef(false);
  const completed = !!app && !!name;
  useEffect(() => {
    if (!app || isInitRef.current) {
      return;
    }

    isInitRef.current = true;
    if (completed) {
      onSuccess?.({
        source: 'init',
      });
    }
  }, [app, completed, onSuccess]);

  const handleSubmit: SubmitHandler<IPackagePreparerNameStepFormValue> = (
    values,
  ) => {
    if (updateMutation.isLoading) {
      return;
    }

    const nameWithPrefix = prefix
      ? values.name?.startsWith(prefix)
        ? values.name
        : `${prefix}${values.name}`
      : values.name;
    if (nameWithPrefix === name) {
      onSuccess?.({ source: 'user' });

      return;
    }

    return updateMutation.mutateAsync(
      {
        appId,
        body:
          platform === 'ios'
            ? { iosName: nameWithPrefix }
            : { androidName: nameWithPrefix },
      },
      {
        onSuccess: onSuccess ? () => onSuccess({ source: 'user' }) : undefined,
      },
    );
  };

  return (
    <Step
      {...other}
      label={t('PackagePreparerNameStep.name')}
      hasError={!!appQuery.error}
      alwaysExpanded={!other.disabled}
    >
      {appQuery.isError ? (
        <ErrorMessage error={appQuery.error} />
      ) : appQuery.isLoading ? (
        t('common.loading')
      ) : app ? (
        ({ active }) =>
          active ? (
            locked ? (
              <Stack gap={4} align="start">
                <Trans
                  parent="div"
                  i18nKey="PackagePreparerNameStep.summary-field.name"
                  values={{ value: name }}
                />
                {locked ? (
                  <p>{t('PackagePreparerNameStep.error.locked')}</p>
                ) : null}
                <Button type="submit" loading={updateMutation.isLoading}>
                  {t('common.next')}
                </Button>
              </Stack>
            ) : (
              <Stack
                as="form"
                gap={6}
                align="start"
                onSubmit={form.handleSubmit(handleSubmit)}
              >
                <Trans
                  parent="p"
                  i18nKey="PackagePreparerNameStep.description"
                />
                <FormField
                  as={TextInputField}
                  forwardError
                  sx={styles.field}
                  control={form.control}
                  name="name"
                  label={t('common.name')}
                  maxLength={28}
                  prefixText={prefix}
                  variant="outlined"
                  defaultValue={defaultName}
                  rules={{ required: true, maxLength: 28 }}
                />
                <Labeled
                  label={t(
                    platform === 'ios'
                      ? 'PackagePreparerNameStep.fields.iosUniqueName.label'
                      : 'PackagePreparerNameStep.fields.androidUniqueName.label',
                  )}
                  supportingText={t(
                    'PackagePreparerNameStep.fields.iosUniqueName.supportingText',
                  )}
                  labelPosition="right"
                >
                  <Checkbox
                    onChange={(_, value) => setIsUniqueName(!!value)}
                    checked={isUniqueName}
                  />
                </Labeled>
                <Button
                  type="submit"
                  loading={updateMutation.isLoading}
                  disabled={!isUniqueName}
                >
                  {t('common.next')}
                </Button>
              </Stack>
            )
          ) : name ? (
            <Stack gap={4} align="start">
              <Trans
                parent="div"
                i18nKey="PackagePreparerNameStep.summary-field.name"
                values={{ value: name }}
              />
            </Stack>
          ) : null
      ) : null}
    </Step>
  );
};

PackagePreparerNameStep.displayName = 'Step';
